import { useState } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, Trash2, DatabaseBackup, Loader2, CloudUpload, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtNum } from "@/lib/format";

const when = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "");
const ENTITY = { shipment: "Shipment", movement: "Stock entry", asin_sale: "ASIN sale", followup: "Follow-up note" };
const short = (v) => (v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));

const Changes = ({ r }) => (r.before || r.after) && !Array.isArray(r.before) ? (
  <div className="mt-1 space-y-0.5 text-[11px]">
    {Object.keys({ ...(r.before || {}), ...(r.after || {}) }).map((k) => (
      <div key={k}><b>{k}</b>: <span className="text-red-600 line-through">{short(r.before?.[k]) || "—"}</span> → <span className="text-emerald-700">{short(r.after?.[k]) || "—"}</span></div>
    ))}
  </div>
) : null;

const History = () => {
  const [f, setF] = useState({ q: "", entity: "", user: "", date_from: "", date_to: "", page: 1 });
  const { data } = useLoad(() => api.get("/audit", { params: f }).then((r) => r.data), [JSON.stringify(f)]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e === "all" ? "" : e, page: 1 }));
  if (!data) return <Loading />;
  const pages = Math.max(1, Math.ceil(data.total / 100));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input value={f.q} onChange={set("q")} placeholder="Search change, ID or user" className="w-64" data-testid="history-search" />
        <SimpleSelect value={f.entity || "all"} onChange={set("entity")} testid="history-entity" options={[["all", "All areas"], ...data.entities.map((e) => [e, e])]} />
        <SimpleSelect value={f.user || "all"} onChange={set("user")} testid="history-user" options={[["all", "All users"], ...data.users.map((u) => [u, u])]} />
        <Input type="date" value={f.date_from} onChange={set("date_from")} className="w-40" data-testid="history-from" />
        <Input type="date" value={f.date_to} onChange={set("date_to")} className="w-40" data-testid="history-to" />
      </div>
      <div className="panel overflow-auto">
        <table className="data-table" data-testid="history-table">
          <thead><tr><th>When</th><th>User</th><th>Area</th><th>Change</th></tr></thead>
          <tbody>{data.rows.map((r) => (
            <tr key={r.id} data-testid={`history-row-${r.id}`}><td className="text-xs">{when(r.when)}</td><td className="text-sm">{r.user}<div className="text-[10px] text-muted-foreground">{r.role}</div></td>
              <td className="text-xs">{r.entity}</td><td className="whitespace-normal text-sm">{r.summary}<Changes r={r} /></td></tr>
          ))}</tbody>
        </table>
        {!data.rows.length && <div className="py-10 text-center text-sm text-muted-foreground">No changes recorded yet.</div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-sm">
        <span>{fmtNum(data.total)} changes · page {f.page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF((x) => ({ ...x, page: x.page - 1 }))}>Prev</Button>
        <Button size="sm" variant="outline" disabled={f.page >= pages} onClick={() => setF((x) => ({ ...x, page: x.page + 1 }))}>Next</Button>
      </div>
    </div>
  );
};

const Bin = () => {
  const { data, reload } = useLoad(() => api.get("/recycle-bin").then((r) => r.data));
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const restore = (b) => act(() => api.post(`/recycle-bin/${b.id}/restore`), "Restored");
  const purge = (b) => window.confirm("Delete this item permanently? It cannot be restored.") && act(() => api.delete(`/recycle-bin/${b.id}`), "Deleted permanently");
  const empty = () => window.prompt('Type EMPTY to permanently delete everything in the recycle bin') === "EMPTY"
    && act(() => api.delete("/recycle-bin", { params: { confirm: "EMPTY" } }), "Recycle bin emptied");
  if (!data) return <Loading />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between"><p className="text-sm text-muted-foreground">Deleted items stay here until you empty the bin. Restoring a stock entry puts the stock back.</p>
        {!!data.length && <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={empty} data-testid="bin-empty-btn"><Trash2 className="h-4 w-4" /> Empty bin</Button>}</div>
      <div className="panel overflow-auto">
        <table className="data-table" data-testid="bin-table">
          <thead><tr><th>Deleted</th><th>By</th><th>Type</th><th>Item</th><th></th></tr></thead>
          <tbody>{data.map((b) => (
            <tr key={b.id} data-testid={`bin-row-${b.id}`}><td className="text-xs">{when(b.deleted_at)}</td><td className="text-sm">{b.deleted_by}</td><td className="text-xs">{ENTITY[b.entity] || b.entity}</td>
              <td className="whitespace-normal text-sm">{b.label}</td>
              <td className="whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => restore(b)} data-testid={`bin-restore-${b.id}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
                <button className="ml-1 p-1.5 text-muted-foreground hover:text-red-600" onClick={() => purge(b)} title="Delete permanently" data-testid={`bin-purge-${b.id}`}><Trash2 className="h-4 w-4" /></button></td></tr>
          ))}</tbody>
        </table>
        {!data.length && <div className="py-10 text-center text-sm text-muted-foreground">Recycle bin is empty.</div>}
      </div>
    </div>
  );
};

const RestoreGuide = () => (
  <details className="panel p-4 text-sm" data-testid="restore-guide">
    <summary className="cursor-pointer font-semibold">How to restore data (step by step)</summary>
    <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
      <li><b>One wrong delete</b> (shipment, note, stock entry, ASIN sale): open the Recycle bin tab and click Restore. Stock is recalculated automatically.</li>
      <li><b>A wrong edit</b>: open Change history, find the entry, and re-enter the old value shown in red.</li>
      <li><b>Check a backup</b>: click Verify next to a backup. It reads the whole file and confirms every record count, without changing any data.</li>
      <li><b>Large mistake or lost data</b>: email support@emergent.sh with your job ID and the date/time to restore to. Emergent can restore the live database to any moment in the last 7 days, or to an hourly/daily/weekly/monthly backup.</li>
      <li><b>Database lost completely</b>: the encrypted off-site copies in your AWS S3 bucket can be restored into a new, empty database with the restore script (backend/scripts/restore_backup.py). You need the backup encryption key, so keep it in a safe place outside this app.</li>
      <li><b>Keep an offline copy</b>: click Download on any backup and store the file safely (it contains no passwords).</li>
    </ol>
  </details>
);

const Offsite = () => {
  const { data, reload } = useLoad(() => api.get("/backups/offsite").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const runTest = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/backups/offsite/test");
      setTest(r);
      if (r.ok) toast.success("S3 connection: all checks passed"); else toast.error("S3 connection: some checks failed");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/backups/offsite");
      if (r.ok) toast.success("Encrypted off-site backup saved and checked"); else toast.error(r.error);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;
  return (
    <div className="panel p-4 space-y-3" data-testid="offsite-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">Off-site encrypted backups (AWS S3)</h3>
          <p className="text-xs text-muted-foreground" data-testid="offsite-status">
            {data.configured ? `Bucket: ${data.bucket} (${data.region})` : "Not connected yet — AWS S3 keys are missing."} Every night at 3:00 AM IST: one consistent snapshot, AES-256-GCM encrypted,
            decrypted and checked before upload, SHA-256 confirmed by S3, never overwrites an existing file. Retention is set by the bucket's lifecycle rules
            (daily 30 days, monthly 12 months). Failure alerts go to {data.alert_email}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runTest} disabled={busy} data-testid="offsite-test-btn">Test S3 connection</Button>
          <Button size="sm" onClick={run} disabled={busy} className="bg-[#071434] hover:bg-[#0d1f4d]" data-testid="offsite-run-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />} Off-site backup now</Button>
        </div>
      </div>
      {test && (
        <ul className="rounded-md border p-3 space-y-1 text-xs" data-testid="offsite-test-results">
          {test.checks.map((c) => (
            <li key={c.check} className="flex items-start gap-2" data-testid={`offsite-check-${c.check.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 mt-0.5" /> : <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5" />}
              <span><b>{c.check}</b>: {c.detail}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="overflow-auto">
        <table className="data-table" data-testid="offsite-table">
          <thead><tr><th>When</th><th>Type</th><th>Result</th><th className="text-right">Size</th><th>File / error</th></tr></thead>
          <tbody>{data.runs.map((r) => (
            <tr key={r.id} data-testid={`offsite-row-${r.id}`}><td className="text-xs">{when(r.at)}</td><td className="text-xs">{r.kind}</td>
              <td>{r.ok ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Saved & checked</span>
                : <span className="inline-flex items-center gap-1 text-red-600 text-xs"><XCircle className="h-3.5 w-3.5" /> Failed</span>}</td>
              <td className="num">{r.size ? `${fmtNum(r.size / 1024)} KB` : "—"}</td>
              <td className="whitespace-normal text-[11px] text-muted-foreground">{r.ok ? r.key : r.error}</td></tr>
          ))}</tbody>
        </table>
        {!data.runs.length && <div className="py-6 text-center text-sm text-muted-foreground">No off-site backups yet.</div>}
      </div>
    </div>
  );
};

const Backups = () => {
  const { data, reload } = useLoad(() => api.get("/backups").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try { await api.post("/backups"); toast.success("Backup created"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;
  const verify = async (b) => {
    try { const { data: r } = await api.post(`/backups/${b.id}/verify`); (r.ok ? toast.success : toast.error)(r.message); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <RestoreGuide />
      <Offsite />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">A backup runs automatically every day at 3:00 AM IST and is kept for 30 days. Passwords are never included. Emergent also keeps its own hourly/daily backups of the live database.</p>
        <Button size="sm" onClick={create} disabled={busy} className="bg-[#071434] hover:bg-[#0d1f4d]" data-testid="backup-create-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Back up now</Button>
      </div>
      <div className="panel overflow-auto">
        <table className="data-table" data-testid="backup-table">
          <thead><tr><th>Created</th><th>Type</th><th>By</th><th className="text-right">Size</th><th>Records</th><th></th></tr></thead>
          <tbody>{data.map((b) => (
            <tr key={b.id} data-testid={`backup-row-${b.id}`}><td className="text-xs">{when(b.created_at)}</td><td className="text-xs">{b.kind === "daily" ? "Daily (auto)" : "Manual"}</td><td className="text-sm">{b.by}</td>
              <td className="num">{fmtNum((b.size || 0) / 1024)} KB</td>
              <td className="whitespace-normal text-[11px] text-muted-foreground">{Object.entries(b.counts || {}).map(([k, v]) => `${k} ${v}`).join(", ")}</td>
              <td className="whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => verify(b)} className="mr-1" data-testid={`backup-verify-${b.id}`}>Verify</Button><Button size="sm" variant="outline" onClick={() => downloadFile(`/backups/${b.id}/download`, {}, b.filename).catch((e) => toast.error(errMsg(e)))} data-testid={`backup-download-${b.id}`}><Download className="h-3.5 w-3.5" /> Download</Button></td></tr>
          ))}</tbody>
        </table>
        {!data.length && <div className="py-10 text-center text-sm text-muted-foreground">No backups yet. The first daily backup runs tonight, or click "Back up now".</div>}
      </div>
    </div>
  );
};

export default function DataSafety() {
  return (
    <div className="max-w-6xl">
      <PageHeader title="History, recycle bin & backups" subtitle="Who changed what, undo deletes, and download full backups. Admin only." />
      <Tabs defaultValue="history">
        <TabsList className="mb-3"><TabsTrigger value="history" data-testid="tab-history">Change history</TabsTrigger>
          <TabsTrigger value="bin" data-testid="tab-bin">Recycle bin</TabsTrigger><TabsTrigger value="backups" data-testid="tab-backups">Backups</TabsTrigger></TabsList>
        <TabsContent value="history"><History /></TabsContent>
        <TabsContent value="bin"><Bin /></TabsContent>
        <TabsContent value="backups"><Backups /></TabsContent>
      </Tabs>
    </div>
  );
}
